// src/DocumentTable.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext'; // (1. Import useAuth)
import toast from 'react-hot-toast';

const API_URL = 'https://rys-backend.onrender.com/api';

// --- (2. ลบ getAuthHeaders เก่าทิ้ง) ---

// (ไอคอน)
const PencilIcon = () => ( <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" /></svg> );
const TrashIcon = () => ( <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg> );
const PlusIcon = () => ( <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" /></svg> );
const PinIcon = () => ( <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v1.586l-1.707 1.707A1 1 0 003 11.707V16a1 1 0 001 1h12a1 1 0 001-1v-4.293a1 1 0 00-.293-.707L15 9.586V7a3 3 0 00-3-3H8zm0 2a1 1 0 011-1h2a1 1 0 011 1v1H8V6z" clipRule="evenodd" /></svg> );

function DocumentTable() {
  // --- (3. ดึง getAuthHeaders จาก useAuth) ---
  const { getAuthHeaders } = useAuth(); 

  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // States for Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // States for Delete Modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingDoc, setDeletingDoc] = useState(null); // (เก็บ {id, title})

  // (4. แก้ไข fetchData)
  const fetchData = useCallback(async (page = 1, query = '') => {
    setIsLoading(true);
    try {
      // (5. เรียกใช้ getAuthHeaders() จาก useAuth)
      const response = await axios.get(`${API_URL}/admin/documents`, {
        params: { page, q: query },
        ...getAuthHeaders() // (ใช้ Token ที่ถูกต้อง)
      });
      setDocuments(response.data.documents);
      setCurrentPage(response.data.current_page);
      setTotalPages(response.data.total_pages);
    } catch (err) {
      toast.error("ไม่สามารถโหลดข้อมูลเอกสารได้");
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeaders]); // (6. เพิ่ม getAuthHeaders ใน dependency)

  useEffect(() => {
    fetchData(1, searchTerm);
  }, [fetchData, searchTerm]); // (fetchData ถูกสร้างด้วย useCallback แล้ว)

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };


  // (ฟังก์ชัน Modal ลบ)
  const handleOpenDeleteModal = (doc) => {
    setDeletingDoc(doc);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingDoc) return;
    try {
      // (7. เรียกใช้ getAuthHeaders() จาก useAuth)
      await axios.delete(`${API_URL}/documents/${deletingDoc.id}`, getAuthHeaders());
      toast.success("ลบเอกสารเรียบร้อย");
      fetchData(currentPage, searchTerm); // (โหลดข้อมูลซ้ำ)
    } catch (err) {
      toast.error("ลบไม่สำเร็จ");
    } finally {
      setIsDeleteModalOpen(false);
      setDeletingDoc(null);
    }
  };
  
  return (
    <div className="container mx-auto p-4 sm:p-6 lg:p-8">
      
      {/* (ส่วน Header และปุ่ม) */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6">
        <h2 className="text-3xl font-semibold text-gray-800 mb-4 md:mb-0">
          จัดการเอกสาร
        </h2>
        <div className="flex space-x-2">
          <Link to="/admin/documents" className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700">
            &larr; ย้อนกลับ
          </Link>
          <Link to="/new" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-1">
            <PlusIcon />
            <span>เพิ่มเอกสารใหม่</span>
          </Link>
        </div>
      </div>

      {/* (ส่วน Search) */}
      <div className="mb-4">
        <input 
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="ค้นหาหัวข้อ หรือ หมวดหมู่..."
          className="w-full p-3 text-lg border border-gray-300 rounded-md shadow-sm"
        />
      </div>

      {/* (ตารางแสดงผล) */}
      <div className="bg-white rounded-lg shadow-xl overflow-x-auto">
        {isLoading ? (
          <p className="p-6 text-center">Loading...</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            {/* (thead) */}
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-base font-medium text-gray-500 uppercase">NO.</th>
                <th className="px-6 py-3 text-left text-base font-medium text-gray-500 uppercase">Title</th>
                <th className="px-6 py-3 text-left text-base font-medium text-gray-500 uppercase">Categories</th>
                <th className="px-6 py-3 text-center text-base font-medium text-gray-500 uppercase">Tags</th>
                <th className="px-6 py-3 text-right text-base font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            {/* (tbody) */}
            <tbody className="bg-white divide-y divide-gray-200">
              {documents.map((doc) => (
                <tr key={doc.id}>
                  <td className="px-6 py-4 text-lg">{doc.id}</td>
                  <td className="px-6 py-4 text-lg font-medium text-gray-900">
                    {doc.is_pinned && <PinIcon />}
                    <span className="ml-2">{doc.title}</span>
                  </td>
                  <td className="px-6 py-4 text-lg text-gray-600">{doc.category}</td>
                  <td className="px-6 py-4 text-lg text-center">{doc.tags_count}</td>
                  <td className="px-6 py-4 text-right space-x-3">
                    <Link to={`/edit/${doc.id}`} className="p-2 text-blue-600 hover:text-blue-900 bg-blue-100 rounded-md inline-block">
                      <PencilIcon />
                    </Link>
                    <button onClick={() => handleOpenDeleteModal(doc)} className="p-2 text-red-600 hover:text-red-900 bg-red-100 rounded-md inline-block">
                      <TrashIcon />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* (ส่วน Pagination) */}
      {/* ... (โค้ด Pagination) ... */}

      {/* (Modal ลบ) */}
      {isDeleteModalOpen && deletingDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white w-full max-w-lg p-6 rounded-lg shadow-xl m-4">
            <h2 className="text-2xl font-semibold mb-5 text-gray-800 border-b pb-3">
              ยืนยันการลบ
            </h2>
            <p className="mb-6 text-lg">
              คุณแน่ใจหรือไม่ว่าต้องการลบเอกสาร: <br/>
              <strong className="text-red-600">{deletingDoc.title}</strong>?
            </p>
            <p className="text-base text-gray-600">
              (การกระทำนี้ไม่สามารถย้อนกลับได้)
            </p>
            <div className="flex justify-end space-x-3 pt-6">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition duration-300"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete} // (เรียกฟังก์ชันลบจริง)
                className="px-5 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition duration-300"
              >
                ยืนยันการลบ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DocumentTable;